import { getCaretPosition, setCaretPosition, escapeHtml } from '../utils/caretUtils';

/**
 * Composable for managing text highlighting in contenteditable elements
 * Supports multiple highlight patterns (e.g., price, date keywords)
 */
export function useTextHighlighting() {
  /**
   * Pattern definitions for different highlight types
   */
  const patterns = {
    price: {
      regex: /\bfor\s+\$?(\d+(?:\.\d{1,2})?)(?=\s|$)/i,
      className: 'price-highlight',
      selector: '.price-highlight'
    },
    date: {
      regex: /\b(yesterday|today|tomorrow)\b/i,
      className: 'date-highlight',
      selector: '.date-highlight'
    },
    tag: {
      regex: /#(\w+)/,
      className: 'tag-highlight',
      selector: '.tag-highlight'
    }
  };

  /**
   * Find all matches in text based on defined patterns
   * @param {string} text - The text to search
   * @returns {Object} Object with matches for each pattern type
   */
  function findTextMatches(text) {
    const matches = {};
    
    for (const [key, pattern] of Object.entries(patterns)) {
      matches[key] = text.match(pattern.regex);
    }
    
    return matches;
  }

  /**
   * Get highlight state for a specific type
   * @param {HTMLElement} element - The contenteditable element
   * @param {string} selector - CSS selector for the highlight
   * @param {Array|null} match - The regex match result
   * @returns {Object} State object with needsUpdate flag
   */
  function getHighlightTypeState(element, selector, match) {
    const highlightElement = element.querySelector(selector);
    const hasHighlight = highlightElement !== null;
    const shouldHighlight = match !== null;

    // No change needed if both states match and content is the same
    if (hasHighlight === shouldHighlight) {
      if (!shouldHighlight) {
        // Both are false - no update needed
        return { needsUpdate: false };
      }
      // Both are true - check if content changed
      const currentText = highlightElement.textContent;
      const newText = match[0];
      return { needsUpdate: currentText !== newText };
    }

    // States don't match - update needed
    return { needsUpdate: true };
  }

  /**
   * Get overall highlight state for all pattern types
   * @param {HTMLElement} element - The contenteditable element
   * @param {Object} matches - Matches object from findTextMatches
   * @returns {Object} State object with needsUpdate flag
   */
  function getHighlightState(element, matches) {
    let needsUpdate = false;

    for (const [key, pattern] of Object.entries(patterns)) {
      const state = getHighlightTypeState(element, pattern.selector, matches[key]);
      if (state.needsUpdate) {
        needsUpdate = true;
        break;
      }
    }

    return { needsUpdate };
  }

  /**
   * Apply all highlights to the element
   * @param {HTMLElement} element - The contenteditable element
   * @param {string} text - The text content
   * @param {Object} matches - Matches object from findTextMatches
   */
  function applyAllHighlights(element, text, matches) {
    // Get current cursor position
    const cursorOffset = getCaretPosition(element);

    // Build the highlighted HTML
    const replacements = [];

    // Collect all matches with their positions
    for (const [key, pattern] of Object.entries(patterns)) {
      const match = matches[key];
      if (match) {
        replacements.push({
          index: match.index,
          length: match[0].length,
          text: match[0],
          className: pattern.className
        });
      }
    }

    // Sort replacements by index (descending) to replace from end to start
    replacements.sort((a, b) => b.index - a.index);

    // Apply replacements
    let result = text;
    for (const replacement of replacements) {
      const before = result.substring(0, replacement.index);
      const after = result.substring(replacement.index + replacement.length);
      result = before + `<span class="${replacement.className}">${escapeHtml(replacement.text)}</span>` + after;
    }

    element.innerHTML = result;

    // Restore cursor position
    setCaretPosition(element, cursorOffset);
  }

  /**
   * Clean description by removing all highlighted keywords
   * @param {string} description - The description text
   * @returns {string} Cleaned description
   */
  function cleanDescription(description) {
    let cleaned = description;

    // Remove all pattern matches
    for (const pattern of Object.values(patterns)) {
      cleaned = cleaned.replace(pattern.regex, '');
    }

    // Clean up extra whitespace
    cleaned = cleaned.replace(/\s+/g, ' ').trim();

    return cleaned;
  }

  /**
   * Add a new pattern type
   * @param {string} key - Pattern identifier
   * @param {RegExp} regex - Regular expression for matching
   * @param {string} className - CSS class for highlighting
   */
  function addPattern(key, regex, className) {
    patterns[key] = {
      regex,
      className,
      selector: `.${className}`
    };
  }

  return {
    patterns,
    findTextMatches,
    getHighlightTypeState,
    getHighlightState,
    applyAllHighlights,
    cleanDescription,
    addPattern
  };
}
